import firebaseConfig from '../../firebase-applet-config.json';
import { supabase } from '../lib/supabase';
import { auth, db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  updateEmail, 
  updatePassword, 
  updateProfile 
} from 'firebase/auth';
import { AppRole } from '../types/permissions';
import { FirebaseAuthService } from './firebaseAuthService';
import { userManagementService } from './userManagementService';

export interface AdminCreateUserData {
  name: string;
  login: string;
  email?: string;
  password?: string;
  role?: AppRole;
  groupIds?: number[];
  active?: string;
}

export interface AdminUpdateUserData {
  name?: string;
  login?: string;
  email?: string;
  password?: string;
  role?: AppRole;
  groupIds?: number[];
  active?: string;
}

export interface AdminApiHealth {
  ok: boolean;
  supabaseConfigured: boolean;
  hasServiceRoleKey: boolean;
  protectedWithJWT?: boolean;
}

const DEFAULT_ROLE_MAPPING: Record<number, AppRole> = {
  1: 'admin',
  2: 'manager',
  3: 'user',
  4: 'user',
  5: 'user',
  6: 'client',
  7: 'driver',
  8: 'driver'
};

/**
 * Gera senha forte e segura caso não seja informada pelo administrador
 */
function generateSecurePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%!';
  let pwd = 'Vp@';
  for (let i = 0; i < 9; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
}

/**
 * Helper para obter os cabeçalhos de autenticação seguros baseados na sessão
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  try {
    if (auth.currentUser) {
      const fbToken = await auth.currentUser.getIdToken();
      if (fbToken) {
        headers['Authorization'] = `Bearer ${fbToken}`;
        headers['x-user-email'] = auth.currentUser.email || '';
      }
    } else {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    }
  } catch (e) {
    console.warn('Não foi possível obter token de autenticação:', e);
  }

  return headers;
}

function getAdminSupabase() {
  // NUNCA expõe ou lê service_role_key no client-side.
  // Utiliza o cliente seguro do Supabase com as regras RLS e autenticação ativas.
  return supabase;
}

export const adminAuthService = {
  /**
   * Verifica a disponibilidade da API de Administração de Autenticação no servidor
   */
  async checkHealth(): Promise<AdminApiHealth> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/admin/health-auth', { headers });
      if (!res.ok) {
        return { ok: false, supabaseConfigured: true, hasServiceRoleKey: false };
      }
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return await res.json();
      }
      return { ok: res.ok, supabaseConfigured: true, hasServiceRoleKey: false };
    } catch (e) {
      return { ok: false, supabaseConfigured: false, hasServiceRoleKey: false };
    }
  },

  salvarUsuariosLocal(): void {},
  obterUsuariosLocal(): any[] { return []; },

  /**
   * Recupera os IDs de usuários excluídos gravados no Firestore
   */
  async obterUsuariosExcluidos(): Promise<(number | string)[]> {
    return await FirebaseAuthService.getDeletedUserIdsFromFirestore();
  },

  /**
   * Registra um ID de usuário como excluído no Firestore
   */
  marcarUsuarioExcluido(userId: number, email?: string): void {
    FirebaseAuthService.markUserDeletedInFirestore(userId, email).catch(() => {});
  },

  /**
   * Remove um ID da lista de excluídos no Firestore quando recriado ou atualizado
   */
  desmarcarUsuarioExcluido(userId: number | string): void {
    FirebaseAuthService.unmarkUserDeletedInFirestore(userId).catch(() => {});
  },

  /**
   * Cria um novo usuário (Firebase Auth + Firestore + Supabase) com verificação de ID único e senha forte
   */
  async criarUsuario(data: AdminCreateUserData): Promise<{ success: boolean; message?: string; user?: any }> {
    try {
      if (!data.name?.trim()) {
        return { success: false, message: 'Nome é obrigatório.' };
      }

      const rawEmail = data.email?.trim() || '';
      let userEmail = rawEmail.toLowerCase();

      // Sanitiza o login para garantir que caracteres especiais, acentos e espaços não criem um e-mail inválido
      const cleanLogin = (data.login?.trim() || (rawEmail.includes('@') ? rawEmail.split('@')[0] : '') || data.name)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // remove acentos
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '.') // substitui caracteres inválidos por ponto
        .replace(/\.+/g, '.')
        .replace(/^\.+|\.+$/g, '');

      const calculatedLogin = cleanLogin || `usuario.${Date.now()}`;

      if (!userEmail || !userEmail.includes('@')) {
        userEmail = `${calculatedLogin}@voopetrans.com.br`;
      }

      // Validação de formato de e-mail padrão RFC 5322 simplificado
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(userEmail)) {
        return { 
          success: false, 
          message: `O e-mail "${userEmail}" possui formato inválido. Verifique se digitou um e-mail válido (ex: usuario@empresa.com.br).` 
        };
      }

      const primaryGroupId = Array.isArray(data.groupIds) && data.groupIds.length > 0 ? Number(data.groupIds[0]) : 2;
      const assignedRole = data.role || DEFAULT_ROLE_MAPPING[primaryGroupId] || 'user';

      // 1. Gera ID sequencial garantido sem colisão
      const existingUsers = await userManagementService.getUsuariosComPermissoes();
      const existingNumericIds = existingUsers
        .map(u => Number(u.id))
        .filter(n => !isNaN(n) && n > 0 && n < 100000);
      const newUserId = existingNumericIds.length > 0 ? Math.max(...existingNumericIds) + 1 : 100;

      // 2. Garante senha definida ou gera senha forte aleatória
      const userPassword = data.password?.trim() || generateSecurePassword();
      const isAutoGeneratedPassword = !data.password?.trim();

      const newUserObj = {
        id: newUserId,
        name: data.name.trim(),
        login: calculatedLogin.toLowerCase(),
        email: userEmail,
        active: data.active || 'Y',
        auth_user_id: `auth_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        system_unit_id: 1,
        groups: [primaryGroupId],
        role: assignedRole,
        created_at: new Date().toISOString()
      };

      // 3. Cria usuário no Firebase Auth
      try {
        const apiKey = firebaseConfig.apiKey;
        const fbRes = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            password: userPassword,
            returnSecureToken: true
          })
        });
        
        const fbData = await fbRes.json();
        
        if (!fbRes.ok) {
          const errMsg = fbData.error?.message || 'Erro desconhecido';
          if (errMsg.includes('INVALID_EMAIL')) {
             return { success: false, message: `O formato do e-mail (${userEmail}) é inválido perante o Firebase Auth.` };
          }
          if (errMsg.includes('OPERATION_NOT_ALLOWED')) {
             return { success: false, message: 'Erro: O provedor de E-mail/Senha está desativado no Firebase Authentication.' };
          }
          if (errMsg.includes('EMAIL_EXISTS')) {
             return { success: false, message: 'O e-mail informado já está cadastrado no Firebase Auth.' };
          }
          if (errMsg.includes('WEAK_PASSWORD')) {
             return { success: false, message: 'A senha informada é fraca. Ela deve conter pelo menos 6 caracteres.' };
          }
          if (errMsg.includes('TOO_MANY_ATTEMPTS_TRY_LATER')) {
             return { success: false, message: 'Muitas tentativas simultâneas. Por favor, aguarde alguns instantes e tente novamente.' };
          }
          throw new Error(errMsg);
        }
        
        newUserObj.auth_user_id = fbData.localId;
        
        // Atualiza o displayName no Firebase Auth
        if (data.name) {
           await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:update?key=${apiKey}`, {
             method: 'POST',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify({
               idToken: fbData.idToken,
               displayName: data.name.trim(),
               returnSecureToken: false
             })
           });
        }
      } catch (fbAuthErr: any) {
        console.error('Erro ao criar usuário no Firebase Auth via REST:', fbAuthErr);
        return { success: false, message: fbAuthErr.message || 'Falha ao criar autenticação no Firebase.' };
      }

      // 4. Desmarca da lista de excluídos
      await FirebaseAuthService.unmarkUserDeletedInFirestore(newUserId);
      await FirebaseAuthService.unmarkUserDeletedInFirestore(userEmail);

      // 5. Salva perfil no Firestore
      try {
        await FirebaseAuthService.saveUserProfile(newUserObj.auth_user_id, {
          id: newUserId,
          name: data.name.trim(),
          displayName: data.name.trim(),
          login: calculatedLogin.toLowerCase(),
          email: userEmail,
          role: assignedRole,
          active: data.active !== 'N' && data.active !== 'inativo',
          groupIds: [primaryGroupId],
          createdAt: new Date().toISOString()
        });
      } catch (fbSyncErr) {
        console.warn('Aviso ao salvar perfil no Firestore:', fbSyncErr);
      }

      // 6. Cadastra no Supabase de forma complementar
      try {
        const adminClient = getAdminSupabase();
        const { data: insertedUser, error: sysErr } = await adminClient
          .from("system_users")
          .insert({
            id: newUserId,
            name: data.name.trim(),
            login: calculatedLogin,
            email: userEmail,
            active: data.active || 'Y',
            auth_user_id: newUserObj.auth_user_id
          })
          .select()
          .maybeSingle();

        if (!sysErr && insertedUser) {
          await adminClient.from("system_user_group").insert([{
            system_user_id: newUserId,
            system_group_id: primaryGroupId
          }]);
        }
      } catch (err: any) {
        console.warn('Falha ao gravar no Supabase:', err.message);
      }

      const msgDetail = isAutoGeneratedPassword
        ? `Usuário ${data.name} cadastrado com sucesso! Senha gerada: ${userPassword}`
        : `Usuário ${data.name} cadastrado com sucesso!`;

      return {
        success: true,
        message: msgDetail,
        user: newUserObj
      };
    } catch (err: any) {
      console.error('Erro ao cadastrar usuário:', err);
      return { success: false, message: err.message || 'Erro ao processar cadastro de usuário.' };
    }
  },

  /**
   * Atualiza dados de um usuário no Firestore e Supabase
   */
  async atualizarUsuario(userId: number, data: AdminUpdateUserData): Promise<{ success: boolean; message?: string }> {
    try {
      const primaryGroup = Array.isArray(data.groupIds) && data.groupIds.length > 0 ? Number(data.groupIds[0]) : 2;
      const assignedRole = data.role || DEFAULT_ROLE_MAPPING[primaryGroup] || 'user';

      // 1. Desmarca da lista de excluídos do Firestore
      await FirebaseAuthService.unmarkUserDeletedInFirestore(userId);
      if (data.email) {
        await FirebaseAuthService.unmarkUserDeletedInFirestore(data.email.trim().toLowerCase());
      }

      // 2. Atualiza no Firestore
      try {
        const firestoreUsers = await FirebaseAuthService.getAllFirestoreUsers();
        const targetEmail = data.email?.trim().toLowerCase();
        const matchingDoc = firestoreUsers.find(fu => (fu.id && fu.id === userId) || (targetEmail && fu.email.trim().toLowerCase() === targetEmail));

        if (matchingDoc) {
          await FirebaseAuthService.saveUserProfile(matchingDoc.uid, {
            id: userId,
            ...(data.name ? { name: data.name.trim(), displayName: data.name.trim() } : {}),
            ...(data.login ? { login: data.login.trim().toLowerCase() } : {}),
            ...(data.email ? { email: data.email.trim().toLowerCase() } : {}),
            ...(data.active !== undefined ? { active: data.active !== 'N' && data.active !== 'inativo' } : {}),
            ...(data.groupIds ? { groupIds: data.groupIds } : {}),
            role: assignedRole
          });
        }
      } catch (fbSyncErr) {
        console.warn('Aviso ao sincronizar Firestore:', fbSyncErr);
      }

      // 3. Atualizar no Supabase se possível
      try {
        const adminClient = getAdminSupabase();
        const updateSysPayload: any = {};
        if (data.name !== undefined) updateSysPayload.name = data.name.trim();
        if (data.login !== undefined) updateSysPayload.login = data.login.toLowerCase().trim();
        if (data.email !== undefined) updateSysPayload.email = data.email.trim();
        if (data.active !== undefined) updateSysPayload.active = data.active;
        
        if (Object.keys(updateSysPayload).length > 0) {
          await adminClient.from("system_users").update(updateSysPayload).eq("id", userId);
        }

        if (Array.isArray(data.groupIds) && data.groupIds.length > 0) {
          await adminClient.from("system_user_group").delete().eq("system_user_id", userId);
          const records = data.groupIds.map((gid: number) => ({ system_user_id: userId, system_group_id: Number(gid) }));
          await adminClient.from("system_user_group").insert(records);
        }
      } catch (err: any) {
        console.warn('Aviso ao atualizar no Supabase:', err.message);
      }

      // 4. Redefinir senha (Firebase Auth) — via servidor, único lugar com
      // permissão para trocar a senha de OUTRO usuário sem estar logado como ele.
      if (data.password?.trim()) {
        if (data.password.trim().length < 6) {
          return { success: false, message: 'A senha deve ter pelo menos 6 caracteres.' };
        }
        try {
          const headers = await getAuthHeaders();
          const res = await fetch(`/api/admin/users/${userId}/password`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ password: data.password.trim() }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) {
            return { success: false, message: json.error || 'Falha ao redefinir a senha do usuário.' };
          }
        } catch (pwdErr: any) {
          return { success: false, message: pwdErr.message || 'Erro de conexão ao redefinir a senha.' };
        }
      }

      return {
        success: true,
        message: 'Usuário atualizado com sucesso!'
      };
    } catch (err: any) {
      console.error(`Erro ao atualizar usuário ${userId}:`, err);
      return { success: false, message: err.message || 'Erro ao processar atualização.' };
    }
  },

  /**
   * Exclui usuário marcando no Firestore deleted_users e removendo perfil em todos os repositórios
   */
  async excluirUsuario(userId: number, email?: string, uid?: string): Promise<{ success: boolean; message?: string }> {
    try {
      // 1. Busca perfil no Firestore ou Supabase para obter UID e e-mail se não fornecidos
      let userEmail = email || '';
      let userUid = uid || '';

      if (!userEmail || !userUid) {
        const firestoreUsers = await FirebaseAuthService.getAllFirestoreUsers();
        const matchingDoc = firestoreUsers.find(fu => fu.id === userId || (fu.email && fu.email.toLowerCase() === email?.toLowerCase()));
        if (matchingDoc) {
          userEmail = userEmail || matchingDoc.email;
          userUid = userUid || matchingDoc.uid;
        }
      }

      if (!userEmail) {
        try {
          const { data: sysUser } = await supabase
            .from('system_users')
            .select('email, auth_user_id')
            .eq('id', userId)
            .maybeSingle();
          if (sysUser) {
            userEmail = sysUser.email || '';
            userUid = userUid || sysUser.auth_user_id || '';
          }
        } catch (e) {
          // Ignora erro
        }
      }

      // 2. Grava no Firestore deleted_users para bloquear autenticação imediatamente
      await FirebaseAuthService.markUserDeletedInFirestore(userId, userEmail, userUid);

      // 3. Remove documento da coleção users no Firestore
      await FirebaseAuthService.deleteUserProfile(userUid, userEmail, userId);

      // 4. Remove via Backend API se disponível
      try {
        const headers = await getAuthHeaders();
        await fetch(`/api/admin/users/${userId}`, {
          method: 'DELETE',
          headers
        });
      } catch (apiErr) {
        console.warn('API backend delete not available or error:', apiErr);
      }

      // 5. Remove do Supabase
      try {
        const adminClient = getAdminSupabase();
        await adminClient.from("system_user_group").delete().eq("system_user_id", userId);
        await adminClient.from("system_user_program").delete().eq("system_user_id", userId);
        await adminClient.from("system_preference").delete().eq("id", `user_perms_${userId}`);
        await adminClient.from("system_users").delete().eq("id", userId);
      } catch (err: any) {
        console.warn('Aviso ao excluir no Supabase:', err.message);
      }

      return {
        success: true,
        message: 'Usuário removido e acesso revogado com sucesso.'
      };
    } catch (err: any) {
      console.error(`Erro ao excluir usuário ${userId}:`, err);
      return { success: false, message: err.message || 'Erro ao processar exclusão.' };
    }
  },

  /**
   * Restaura o usuário removendo o bloqueio do Firestore e reativando seu perfil
   */
  async restaurarUsuario(identifier: number | string, email?: string): Promise<{ success: boolean; message?: string }> {
    try {
      await FirebaseAuthService.unmarkUserDeletedInFirestore(identifier);
      if (email) {
        await FirebaseAuthService.unmarkUserDeletedInFirestore(email);
        await FirebaseAuthService.saveUserProfile(email, {
          email,
          active: true
        });
      }

      return {
        success: true,
        message: 'Usuário restaurado com sucesso.'
      };
    } catch (err: any) {
      console.error('Erro ao restaurar usuário:', err);
      return { success: false, message: err.message || 'Erro ao restaurar usuário.' };
    }
  },

  /**
   * Sincroniza e restaura todos os usuários do Firebase
   */
  async sincronizarUsuariosFirebase(): Promise<{ success: boolean; message?: string; count?: number }> {
    try {
      const users = await FirebaseAuthService.syncAndRestoreAllFirebaseUsers();
      return {
        success: true,
        message: `${users.length} usuários sincronizados com sucesso do Firebase.`,
        count: users.length
      };
    } catch (err: any) {
      console.error('Erro ao sincronizar usuários do Firebase:', err);
      return { success: false, message: err.message || 'Erro ao sincronizar do Firebase.' };
    }
  },

  /**
   * Permite que o próprio usuário logado altere seu nome, e-mail e senha de forma segura com reautenticação
   */
  async alterarMinhasCredenciais(params: {
    email?: string;
    newPassword?: string;
    currentPassword?: string;
    nome?: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { success: false, message: 'Nenhum usuário autenticado encontrado.' };
      }

      let emailChanged = false;
      let passwordChanged = false;
      let nameChanged = false;

      // Se for alterar e-mail ou senha, exige a senha atual e reautentica o usuário
      if (params.newPassword?.trim() || (params.email?.trim() && params.email.trim().toLowerCase() !== currentUser.email?.toLowerCase())) {
        if (!params.currentPassword) {
          return {
            success: false,
            message: 'A senha atual é obrigatória para confirmar a alteração de e-mail ou nova senha.'
          };
        }

        const credential = EmailAuthProvider.credential(currentUser.email!, params.currentPassword);
        await reauthenticateWithCredential(currentUser, credential);
      }

      // 1. Atualizar Nome
      if (params.nome?.trim()) {
        const newName = params.nome.trim();
        await updateProfile(currentUser, { displayName: newName });
        await FirebaseAuthService.saveUserProfile(currentUser.uid, {
          displayName: newName,
          name: newName
        });
        nameChanged = true;
      }

      // 2. Atualizar E-mail
      if (params.email?.trim() && params.email.trim().toLowerCase() !== currentUser.email?.toLowerCase()) {
        const newEmail = params.email.trim().toLowerCase();
        await updateEmail(currentUser, newEmail);
        await FirebaseAuthService.saveUserProfile(currentUser.uid, { email: newEmail });
        emailChanged = true;
      }

      // 3. Atualizar Senha
      if (params.newPassword?.trim()) {
        await updatePassword(currentUser, params.newPassword.trim());
        passwordChanged = true;
      }

      const changedFields: string[] = [];
      if (nameChanged) changedFields.push('nome');
      if (emailChanged) changedFields.push('e-mail');
      if (passwordChanged) changedFields.push('senha');

      if (changedFields.length === 0) {
        return { success: true, message: 'Nenhum campo foi alterado.' };
      }

      return {
        success: true,
        message: `Credenciais atualizadas com sucesso (${changedFields.join(', ')})!`
      };
    } catch (e: any) {
      console.error('Erro ao alterar credenciais:', e);
      if (e?.code === 'auth/wrong-password' || e?.code === 'auth/invalid-credential') {
        return { success: false, message: 'Senha atual incorreta. Por favor, tente novamente.' };
      }
      if (e?.code === 'auth/requires-recent-login') {
        return { success: false, message: 'Sessão expirada. Por segurança, faça login novamente para alterar suas credenciais.' };
      }
      return { success: false, message: e.message || 'Erro ao atualizar credenciais do usuário.' };
    }
  }
};
